import { create } from "@bufbuild/protobuf";
import type { Message } from "@bufbuild/protobuf";
import { WeaponProgressSchema } from "../../../api/types";
import type { RankTier, UnlockGroup, UnlockItem, WeaponId, WeaponInfo, WeaponProgress } from "../../../api/types";

// ---- Lifetime stats ----
//
// Fractions are 0..1 and `null` when the divisor is zero (nothing to show yet).

/**
 * Kills per death. Without deaths it equals the kill count (the convention of `formatRatio`);
 * `null` when there are neither kills nor deaths.
 */
export function kdRatio(kills: number, deaths: number): number | null {
  if (deaths > 0) return kills / deaths;
  return kills > 0 ? kills : null;
}

export function winRate(wins: number, matches: number): number | null {
  return matches > 0 ? wins / matches : null;
}

export function headshotRate(headshots: number, kills: number): number | null {
  return kills > 0 ? headshots / kills : null;
}

export function killsPerMatch(kills: number, matches: number): number | null {
  return matches > 0 ? kills / matches : null;
}

// ---- Rank ----

/** The next tier as a plain value (a `RankTier` without the message brand) plus the missing rating. */
export interface NextRankTier extends Omit<RankTier, keyof Message> {
  /** Whole rating points still missing (rounded up, so it is never 0 before the tier is reached). */
  remaining: number;
}

/**
 * The tier above the one `rating` falls into, or `null` at the top tier. A tier starts at its
 * `minRating` (exactly that rating already belongs to it). The lowest tier has no lower bound,
 * so it is never "next", even for a negative rating.
 */
export function nextRankTier(tiers: readonly RankTier[], rating: number): NextRankTier | null {
  const ascending = [...tiers].sort((a, b) => a.minRating - b.minRating);
  const next = ascending.find((tier, i) => i > 0 && tier.minRating > rating);
  if (!next) return null;
  return { name: next.name, minRating: next.minRating, remaining: Math.ceil(next.minRating - rating) };
}

// ---- Weapon levels ----

export interface WeaponLevelProgress {
  /** XP earned since the weapon reached its current level. */
  intoLevel: number;
  /** XP between the current level and the next one; 0 at max level (no next table entry). */
  forNext: number;
}

/**
 * Progress to the next weapon level. `weaponLevelXp[i]` is the total XP for weapon level `i + 1`,
 * so the next level exists while `weaponLevelXp[level]` does.
 */
export function weaponLevelProgress(
  xp: number,
  level: number,
  weaponLevelXp: readonly number[],
): WeaponLevelProgress {
  const current = Math.max(1, level);
  if (current >= weaponLevelXp.length) {
    const base = weaponLevelXp[weaponLevelXp.length - 1] ?? 0;
    return { intoLevel: Math.max(0, xp - base), forNext: 0 };
  }
  const base = weaponLevelXp[current - 1];
  const forNext = weaponLevelXp[current] - base;
  return { intoLevel: Math.min(Math.max(0, xp - base), forNext), forNext };
}

export interface WeaponUnlockHint {
  kind: "attachment" | "camo";
  id: string;
  /** Weapon level that unlocks it. */
  level: number;
}

/**
 * The next attachment or camo a weapon at `weaponLevel` will unlock: the lowest required weapon
 * level above it (attachments win ties), `null` when everything is unlocked.
 */
export function nextWeaponUnlock(
  weapon: Pick<WeaponInfo, "allowedAttachments" | "attachmentLevels">,
  weaponLevel: number,
  camoLevels: Readonly<Record<string, number>>,
): WeaponUnlockHint | null {
  let best: WeaponUnlockHint | null = null;
  for (let i = 0; i < weapon.allowedAttachments.length; i++) {
    const level = weapon.attachmentLevels[i];
    if (level > weaponLevel && (best === null || level < best.level)) {
      best = { kind: "attachment", id: weapon.allowedAttachments[i], level };
    }
  }
  for (const [id, level] of Object.entries(camoLevels)) {
    if (level > weaponLevel && (best === null || level < best.level)) {
      best = { kind: "camo", id, level };
    }
  }
  return best;
}

/**
 * One row per weapon with progress plus every unlocked weapon without a row (level 1, no XP),
 * most XP first (ties by weapon id).
 */
export function weaponRows(
  progress: readonly WeaponProgress[],
  unlockedWeapons: readonly WeaponId[],
): WeaponProgress[] {
  const rows = new Map<WeaponId, WeaponProgress>();
  for (const row of progress) rows.set(row.weapon, row);
  for (const weapon of unlockedWeapons) {
    if (!rows.has(weapon)) rows.set(weapon, create(WeaponProgressSchema, { weapon, level: 1 }));
  }
  return [...rows.values()].sort((a, b) => b.xp - a.xp || a.weapon.localeCompare(b.weapon));
}

// ---- Unlock roadmap ----

export type RoadmapState = "reached" | "current" | "locked";

export interface RoadmapNode {
  level: number;
  state: RoadmapState;
  items: readonly UnlockItem[];
}

/**
 * Timeline nodes in ascending level order. Levels before the player's are `reached`, the
 * player's own is `current`, later ones `locked`. When no unlock group sits exactly on the
 * player's level, an empty `current` node is inserted so the position is always visible.
 */
export function roadmapNodes(groups: readonly UnlockGroup[], playerLevel: number): RoadmapNode[] {
  const nodes = groups
    .map(
      (group): RoadmapNode => ({
        level: group.level,
        state: group.level < playerLevel ? "reached" : group.level === playerLevel ? "current" : "locked",
        items: group.items,
      }),
    )
    .sort((a, b) => a.level - b.level);
  const at = nodes.findIndex((node) => node.level >= playerLevel);
  const here: RoadmapNode = { level: playerLevel, state: "current", items: [] };
  if (at === -1) nodes.push(here);
  else if (nodes[at].level !== playerLevel) nodes.splice(at, 0, here);
  return nodes;
}
